import React, { FC } from 'react';
import { TFunction } from 'next-i18next';
import type { Title, Journal } from 'src/common/types/api/v4/search/v1/landing';
import Carousel from 'src/common/components/dockyard/Carousel';
import Typography from 'src/common/components/Typography';
import Icon from 'src/common/components/IconDS22';
import UniversalLink from 'src/common/components/UniversalLink';
import Button from 'src/common/components/ButtonDS22';
import { EXTERNAL_PATHS, NEW_TAB_ATTRIBUTES } from 'src/common/constants';
import JournalImg from 'src/common/components/LandingPageJournal/JournalImg';
import PitchPageLayout from 'src/common/components/PitchPageLayout';
import CarouselButtons from 'src/common/components/dockyard/CarouselButtons';
import { CarouselProvider } from 'src/common/providers/CarouselProvider';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { formatDate } from 'src/common/utils/dateHelpers';
import styles from './LandingPageJournal.module.scss';

export type LandingPageJournalProps = {
  cardCta: string;
  headingSize: 'heading-3' | 'display-5';
  journals: Journal[] | undefined;
  sectionCta?: string;
  title: Title;
  translationFunction?: TFunction;
};

const LandingPageJournal: FC<LandingPageJournalProps> = ({
  cardCta, headingSize, journals, sectionCta, title, translationFunction,
}) => {
  const locale = useDateFnsLocale();

  return (
    <PitchPageLayout backgroundColor="midnight01">
      <div className={styles.root}>
        <CarouselProvider>
          <div className={styles.header}>
            <Typography
              type="h3"
              size={headingSize}
              weight="semibold"
            >
              {title}
            </Typography>
            <div className={styles.buttonsDesktop}>
              <CarouselButtons
                spacing="together"
                variant="primary-blue"
              />
            </div>
          </div>
          <Carousel>
            {journals?.map((journal) => (
              <UniversalLink
                key={journal.id}
                href={journal.meta.html_url}
                className={styles.card}
              >
                {journal.header_image.sizes && (
                  <JournalImg
                    sizes={journal.header_image.sizes}
                    alt={journal.title}
                  />
                )}
                <div className={styles.textContainer}>
                  <Typography
                    type="p"
                    size="body-l"
                    weight="regular"
                  >
                    {formatDate(new Date(journal.meta.first_published_at), 'MMMM dd, yyyy', locale)}
                  </Typography>
                  <Typography
                    type="h3"
                    size="body-xl"
                    weight="semibold"
                  >
                    {translationFunction ? translationFunction(journal.title) : journal.title}
                  </Typography>
                  <div className={styles.bottomLine}>
                    <Typography
                      type="p"
                      size="body-l"
                      weight="semibold"
                    >
                      {cardCta}
                    </Typography>
                    <div className={styles.arrow}>
                      <Icon
                        id="arrow-right"
                      />
                    </div>
                  </div>
                </div>
              </UniversalLink>
            ))}
          </Carousel>
          {sectionCta && (
            <div className={styles.button}>
              <Button
                type="link"
                href={EXTERNAL_PATHS.JOURNAL}
                size="medium"
                variant="tertiary-white"
                {...NEW_TAB_ATTRIBUTES}
              >
                {sectionCta}
              </Button>
            </div>
          )}
        </CarouselProvider>
      </div>
    </PitchPageLayout>
  );
};

export default LandingPageJournal;
